import type { RouterConfig } from '@nuxt/schema';
import type { RouteLocationNormalized, RouteRecordRaw } from 'vue-router';
import { USER_LEVEL_KEY } from '~/api/interface';
import { USER_ADMIN } from '~/constants';
import { SESSION_SOURCE } from '~/constants/config';
import { PageKey } from '~/constants/page';

// https://router.vuejs.org/api/interfaces/routeroptions.html

const routes: RouteRecordRaw[] = [
  {
    name: PageKey.NEWS,
    path: '/news',
    component: () => import('~/pages/news/index.vue'),
    meta: {
      track: TRACK_EVENT.PAGE_NEWS,
      layout: 'exposed',
    },
  },
  // 登录
  {
    name: PageKey.LOGIN,
    path: '/login',
    meta: {
      track: TRACK_EVENT.PAGE_LOGIN,
      trackParams: (to: RouteLocationNormalized) => {
        return {
          isInvited: !!to.query.code,
        };
      },
    },
    component: () => import('~/pages/login/index.vue'),
  },
  // 绑定飞书
  {
    name: PageKey.LOGIN_FEISHU,
    path: '/login-feishu',
    meta: {
      layout: 'blank',
      // track: TRACK_EVENT.PAGE_LOGIN,
      // trackParams: (to: RouteLocationNormalized) => {
      //   return {
      //     isInvited: !!to.query.code,
      //   };
      // },
    },
    component: () => import('~/pages/login/login-feishu.vue'),
  },
  // 个人主页
  {
    name: PageKey.PERSONAL_PAGE,
    path: '/personal-page/:key.html',
    component: () => import('~/pages/personal-page/index.vue'),
    meta: {
      layout: 'exposed',
      trackEvent: TRACK_EVENT.PAGE_PERSONAL_PAGE_VIEW,
    },
  },
  // AI 视频创作页
  {
    name: PageKey.AI_AVATAR_VIDEO_GENERATION,
    path: '/ai-avatar-video-generation',
    component: () => import('~/pages/ai-video-cn/index.vue'),
    meta: {
      layout: 'landing',
    },
  },
  // 模板管理
  {
    name: PageKey.TEMPLATE,
    path: '/template',
    component: () => import('~/pages/template/index.vue'),
    meta: {
      layout: 'exposed',
      track: TRACK_EVENT.PAGE_TEMPLATE,
    },
  },
  {
    name: PageKey.TEMPLATE_INFO,
    path: '/template/:id.html',
    component: () => import('~/pages/template-detail/index.vue'),
    meta: {
      layout: 'blank',
      track: TRACK_EVENT.PAGE_TEMPLATE_DETAIL,
    },
  },
  {
    name: PageKey.TEMPLATE_MOBILE_INFO,
    path: '/template/m/:id.html',
    component: () => import('~/pages/template/info-m.vue'),
    meta: {
      layout: 'h5',
      track: TRACK_EVENT.PAGE_TEMPLATE_DETAIL,
    },
  },
  {
    name: PageKey.TEMPLATE_INDUSTRY_SCENE,
    path: '/templates/:industry/:scene?/:page?',
    component: () => import('~/pages/template/group.vue'),
    meta: {
      layout: 'exposed',
    },
  },
  {
    name: PageKey.TEMPLATE_TAG,
    path: '/tags/:tag.html',
    component: () => import('~/pages/template/tag.vue'),
    meta: {
      layout: 'exposed',
    },
  },
  // 付费订阅
  {
    name: PageKey.SUBSCRIBE,
    path: '/subscribe',
    component: () => import('~/pages/subscribe/index.vue'),
    meta: {
      layout: 'exposed',
      track: TRACK_EVENT.PAGE_SUBSCRIBE,
    },
  },
  // 活动教程
  {
    name: PageKey.TUTORIAL,
    path: '/tutorial',
    component: () => import('~/pages/tutorial/index.vue'),
    meta: {
      layout: 'exposed',
      track: TRACK_EVENT.PAGE_TUTORIAL,
      trackParams: () => {
        return {
          position: '首页卡片',
        };
      },
    },
  },
  {
    name: PageKey.TUTORIAL_INFO,
    path: '/tutorial/:id.html',
    component: () => import('~/pages/tutorial/info.vue'),
    meta: {
      layout: 'exposed',
      track: TRACK_EVENT.PAGE_TUTORIAL,
    },
  },
  {
    path: '/activity',
    redirect: '/tutorial',
  },
  {
    path: '/activity/:id.html',
    redirect: '/activity/:id.html',
  },
  // 创作指南
  {
    name: PageKey.PRODUCT_GUIDE,
    path: '/product-guide',
    component: () => import('~/pages/product-guide/index.vue'),
    meta: {
      layout: 'exposed',
      track: TRACK_EVENT.ROUTE_PRODUCT_GUIDE,
    },
  },
  // 帮助中心
  {
    name: PageKey.HELP_CENTER,
    path: '/help-center',
    component: () => import('~/pages/help-center/index.vue'),
    meta: {
      layout: 'exposed',
      track: TRACK_EVENT.ROUTE_HELP,
    },
  },

  // 帮助中心-详情页
  {
    name: PageKey.HELP_CENTER_DETAIL,
    path: '/help-center/:id.html',
    component: () => import('~/pages/help-center/detail.vue'),
    meta: {
      layout: 'exposed',
      // track: TRACK_EVENT.ROUTE_HELP,
    },
  },
  // 帮助中心-预览
  {
    name: PageKey.HELP_CENTER_PREVIEW,
    path: '/help-center-preview/:id.html',
    component: () => import('~/pages/help-center/detail.vue'),
    meta: {
      layout: 'exposed',
      track: TRACK_EVENT.ROUTE_HELP,
    },
  },
  {
    name: PageKey.HOME_BANNER_DETAIL,
    path: '/information/:id.html',
    component: () => import('~/pages/help-center/detail.vue'),
    meta: {
      layout: 'exposed',
      track: TRACK_EVENT.ROUTE_HELP,
    },
  },

  // 账号管理
  {
    name: PageKey.CENTER_ACCOUNT,
    path: '/center/account',
    component: () => import('~/pages/center/account/index.vue'),
    meta: {
      layout: 'transition',
      track: TRACK_EVENT.ROUTE_ACCOUNT_MANGE,
    },
  },
  // 账号认证
  {
    name: PageKey.ACCOUNT_VERIFY,
    path: '/center/account/verify',
    component: () => import('~/pages/center/verify/index.vue'),
    meta: {
      layout: 'transition',
      track: TRACK_EVENT.ROUTE_ACCOUNT_VERIFY,
      parent: '/center/account',
      permission: {
        role: [USER_ADMIN.ORG_ADMIN_USER, USER_ADMIN.SUPER_ADMIN, USER_ADMIN.MANAGEMENT_PLATFORM],
        level: [
          USER_LEVEL_KEY.ORG_STANDARD,
          USER_LEVEL_KEY.ORG_FLAGSHIP,
          USER_LEVEL_KEY.TEAM_LIGHT,
          USER_LEVEL_KEY.TEAM_TASTE,
        ],
      },
    },
  },
  // 账号认证结果
  {
    name: PageKey.ACCOUNT_VERIFY_RESULT,
    path: '/center/account/result',
    component: () => import('~/pages/center/verify/index.vue'),
    meta: {
      layout: 'transition',
      track: TRACK_EVENT.ROUTE_ACCOUNT_RESULT,
      parent: '/center/account',
      permission: {
        role: [USER_ADMIN.ORG_ADMIN_USER, USER_ADMIN.SUPER_ADMIN, USER_ADMIN.MANAGEMENT_PLATFORM],
        level: [
          USER_LEVEL_KEY.ORG_STANDARD,
          USER_LEVEL_KEY.ORG_FLAGSHIP,
          USER_LEVEL_KEY.TEAM_LIGHT,
          USER_LEVEL_KEY.TEAM_TASTE,
        ],
      },
    },
  },
  // 第三方平台授权
  {
    name: PageKey.AUTHORIZE,
    path: '/center/auth',
    component: () => import('~/pages/center/auth/index.vue'),
    meta: {
      layout: 'exposed',
    },
  },
  {
    name: PageKey.THIRD_AUTH_LOGIN,
    path: '/auth',
    component: () => import('~/pages/auth/index.vue'),
    meta: {
      layout: false,
    },
  },
  // 成员管理
  {
    name: PageKey.MEMBER_MANAGE,
    path: '/center/member',
    component: () => import('~/pages/center/member/index.vue'),
    meta: {
      layout: 'transition',
      permission: {
        role: [USER_ADMIN.ORG_ADMIN_USER, USER_ADMIN.SUPER_ADMIN, USER_ADMIN.MANAGEMENT_PLATFORM],
        level: [
          USER_LEVEL_KEY.ORG_STANDARD,
          USER_LEVEL_KEY.ORG_FLAGSHIP,
          USER_LEVEL_KEY.TEAM_LIGHT,
          USER_LEVEL_KEY.TEAM_TASTE,
        ],
      },
      // track: TRACK_EVENT.PAGE_TEAM_MANEGE,
    },
  },
  // 消耗记录
  {
    name: PageKey.CENTER_CONSUME,
    path: '/center/consume',
    component: () => import('~/pages/center/consume/index.vue'),
    meta: {
      layout: 'transition',
      permission: {
        role: [USER_ADMIN.NORMAL],
        level: [
          USER_LEVEL_KEY.TASTE,
          USER_LEVEL_KEY.PERSONAL,
          USER_LEVEL_KEY.ORG_STANDARD,
          USER_LEVEL_KEY.ORG_FLAGSHIP,
          USER_LEVEL_KEY.TEAM_LIGHT,
          USER_LEVEL_KEY.TEAM_TASTE,
          USER_LEVEL_KEY.PERSONAL_LITE,
        ],
      },
      track: TRACK_EVENT.ROUTE_CONSUME,
    },
  },
  {
    name: PageKey.INVITE,
    path: '/center/invite',
    component: () => import('~/pages/center/member/invite.vue'),
    meta: {
      layout: 'exposed',
    },
  },
  // 权益管理
  {
    name: PageKey.CENTER_EQUITY,
    path: '/center/equity',
    component: () => import('~/pages/center/equity/index.vue'),
    meta: {
      layout: 'transition',
      permission: {
        role: [USER_ADMIN.ORG_ADMIN_USER, USER_ADMIN.SUPER_ADMIN, USER_ADMIN.MANAGEMENT_PLATFORM],
        level: [
          USER_LEVEL_KEY.TASTE,
          USER_LEVEL_KEY.PERSONAL,
          USER_LEVEL_KEY.ORG_STANDARD,
          USER_LEVEL_KEY.ORG_FLAGSHIP,
          USER_LEVEL_KEY.TEAM_LIGHT,
          USER_LEVEL_KEY.TEAM_TASTE,
          USER_LEVEL_KEY.PERSONAL_LITE,
        ],
      },
    },
  },
  // 订单
  {
    name: PageKey.CENTER_ORDER,
    path: '/center/order',
    component: () => import('~/pages/center/order/index.vue'),
    meta: {
      layout: 'transition',
      track: TRACK_EVENT.PAGE_ORDER_MANAGE,
      permission: {
        role: [USER_ADMIN.ORG_ADMIN_USER, USER_ADMIN.SUPER_ADMIN, USER_ADMIN.MANAGEMENT_PLATFORM],
        level: [
          USER_LEVEL_KEY.TASTE,
          USER_LEVEL_KEY.PERSONAL,
          USER_LEVEL_KEY.ORG_STANDARD,
          USER_LEVEL_KEY.ORG_FLAGSHIP,
          USER_LEVEL_KEY.TEAM_LIGHT,
          USER_LEVEL_KEY.PERSONAL_LITE,
          USER_LEVEL_KEY.TEAM_TASTE,
        ],
      },
    },
  },
  // 开票
  {
    name: PageKey.CENTER_ORDER_INVOICE,
    path: '/center/order/invoice',
    component: () => import('~/pages/center/order/billing.vue'),
    meta: {
      layout: 'transition',
      track: TRACK_EVENT.PAGE_ORDER_MANAGE,
      permission: {
        role: [USER_ADMIN.ORG_ADMIN_USER, USER_ADMIN.SUPER_ADMIN, USER_ADMIN.MANAGEMENT_PLATFORM],
        level: [
          USER_LEVEL_KEY.PERSONAL_LITE,
          USER_LEVEL_KEY.TASTE,
          USER_LEVEL_KEY.PERSONAL,
          USER_LEVEL_KEY.ORG_STANDARD,
          USER_LEVEL_KEY.ORG_FLAGSHIP,
          USER_LEVEL_KEY.TEAM_LIGHT,
          USER_LEVEL_KEY.TEAM_TASTE,
        ],
      },
    },
  },
  // 申请试用
  {
    name: PageKey.APPLY_CONTACT,
    path: '/applyContact',
    component: () => import('~/pages/applyContact/index.vue'),
    meta: {
      layout: 'exposed',
    },
  },
  // 拉新活动
  {
    name: PageKey.PULL_NEW,
    path: '/pull-new',
    component: () => import('~/pages/marketing/pull-new/index.vue'),
    meta: {
      layout: false,
    },
  },
  // 国风创作比赛
  {
    name: PageKey.NATIONAL_RACE,
    path: '/national-race',
    component: () => import('~/pages/marketing/national-race/index.vue'),
    meta: {
      layout: false,
    },
  },
  // 山东5g创新大赛活动
  {
    name: PageKey.INNOVATION_5G,
    path: '/shandong-innovation',
    component: () => import('~/pages/marketing/shandong/index.vue'),
    meta: {
      layout: 'exposed',
    },
  },
  // 团队体验版创建
  {
    name: PageKey.TEAM_TASTE_CREATE,
    path: '/team-taste-create',
    component: () => import('~/pages/teamCreate/taste.vue'),
    meta: {
      layout: 'exposed',
    },
  },
  // 团队入门版创建
  {
    name: PageKey.TEAM_STARTER_CREATE,
    path: '/team-starter-create',
    component: () => import('~/pages/teamCreate/starter.vue'),
    meta: {
      layout: 'exposed',
    },
  },
  // 支付的回调页面
  {
    name: PageKey.PAYMENT_RESULT,
    path: '/paymentResult',
    component: () => import('~/pages/payment-result/index.vue'),
    meta: {
      layout: false,
    },
  },
  {
    name: PageKey.CANCEL_RESULT,
    path: '/cancelResult',
    component: () => import('~/pages/cancel-result/index.vue'),
    meta: {
      layout: false,
    },
  },
  // 客户案例
  {
    name: PageKey.CASE,
    path: '/customer-stories',
    component: () => import('~/pages/case/index.vue'),
    meta: {
      layout: 'exposed',
      track: TRACK_EVENT.PAGE_ENTERPRISE_CASE_LIST,
    },
  },
  // 案例详情
  {
    name: PageKey.CASE_DETAIL,
    path: '/customer-stories/:post.html',
    component: () => import('~/pages/post/case-detail.vue'),
    meta: {
      layout: 'exposed',
    },
  },
  // ppt-world活动
  {
    name: PageKey.PPT_WORLD,
    path: '/ppt-world',
    component: () => import('~/pages/pptWorld/index.vue'),
  },
  // 感谢页 虚拟路由
  {
    name: PageKey.THANKS,
    path: '/login/thanks',
    redirect: '/platform',
  },
  // 成片详情
  {
    name: PageKey.PIECES_DETAIL,
    path: '/pieces/:id.html',
    component: () => import('~/pages/pieces/info.vue'),
  },
  // 授权书签署
  {
    name: PageKey.ACCREDIT,
    path: '/accredit/:id',
    component: () => import('~/pages/accredit/index.vue'),
    meta: {
      layout: 'exposed',
      track: TRACK_EVENT.ROUTE_ACCREDIT,
    },
  },
  // 没权限
  {
    name: PageKey.NO_PERMISSION,
    path: '/no-permission',
    component: () => import('~/pages/special/noPermission.vue'),
    meta: {
      layout: false,
    },
  },
  {
    name: PageKey.NOT_FOUND,
    path: '/not-found',
    component: () => import('~/pages/special/notFound.vue'),
    meta: {
      layout: false,
    },
  },
  // 海外留资
  {
    name: PageKey.CONTACT_US,
    path: '/contact-us',
    component: () => import('~/pages/overseas-contact/index.vue'),
    meta: {
      layout: 'exposed',
    },
  },
  // 全站搜索结果
  {
    name: PageKey.SITE_SEARCH,
    path: '/site-search',
    component: () => import('~/pages/site-search/index.vue'),
    meta: {
      layout: 'exposed',
    },
  },
  // 魔珐学院
  {
    name: PageKey.ACADEMY,
    path: '/academy',
    component: () => import('~/pages/academy/index.vue'),
    meta: {
      layout: 'exposed',
    },
  },
  // 新博客详情页面
  {
    name: PageKey.BLOG,
    path: '/blog/:post.html',
    component: () => import('~/pages/post/blog-detail.vue'),
    meta: {
      layout: 'exposed',
      track: TRACK_EVENT.ROUTE_HELP,
    },
  },
  // // 新博客详情页面
  // {
  //   name: PageKey.BLOG,
  //   path: '/blog/:id.html',
  //   component: () => import('~/pages/post/blog-detail.vue'),
  //   meta: {
  //     layout: 'exposed',
  //     track: TRACK_EVENT.ROUTE_HELP,
  //   },
  // },
  // TODO 是否移除
  {
    name: PageKey.ACADEMY_DETAIL,
    path: '/academy/:collection/:id.html',
    component: () => import('~/pages/help-center/detail.vue'),
    meta: {
      layout: 'exposed',
      track: TRACK_EVENT.ROUTE_HELP,
    },
  },
  {
    name: PageKey.ACADEMY_COLLECTION,
    path: '/category/:collection',
    component: () => import('~/pages/academy/collection.vue'),
    meta: {
      layout: 'exposed',
    },
  },

  // 应用场景
  {
    name: PageKey.SCENARIOS,
    path: '/scenarios/:id.html',
    component: () => import('~/pages/scenarios-new/index.vue'),
    meta: {
      layout: 'exposed',
    },
  },
  {
    name: PageKey.CHARACTER,
    path: '/characters/:industryScene?',
    component: () => import('~/pages/character/index.vue'),
    meta: {
      layout: 'exposed',
    },
  },
  {
    name: PageKey.CHARACTER_DETAIL,
    path: '/3d-avatar/:look_name.html',
    component: () => import('~/pages/character/detail.vue'),
    meta: {
      layout: 'landing',
    },
  },
  {
    name: PageKey.AI_AVATAR,
    path: '/3d-ai-avatar',
    component: () => import('~/pages/ai-avatar-cn/index.vue'),
    meta: {
      layout: 'landing',
    },
  },
  {
    name: PageKey.AVATAR_LIBRARY,
    path: '/3d-ai-avatar/avatar-library/:industryScene?',
    component: () => import('~/pages/character/index.vue'),
    meta: {
      layout: 'exposed',
    },
  },
  {
    name: PageKey.LAB_SHARE_LANDING,
    path: '/share/lab',
    component: () => import('~/pages/lab-share/index.vue'),
    meta: {
      layout: 'blank',
    },
  },
  {
    name: PageKey.SCENE_DETAILS,
    path: '/scene-details',
    component: () => import('~/pages/scene-details/index.vue'),
    meta: {
      layout: 'blank',
    },
  },
];

export default <RouterConfig>{
  routes: _routes => {
    const req = useRequestURL();
    const isPureHost = PURE_HOST.some(item => item.test(req.host));

    const commonRoutes = [...routes];

    let landingRoute: RouteRecordRaw = {
      name: PageKey.LANDING,
      path: '/',
      component: () => import('~/pages/main-cn/index.vue'),
      meta: {
        layout: 'landing',
        track: TRACK_EVENT.PAGE_LANDING_PAGE,
        trackParams: () => {
          return {
            source: sessionStorage.getItem(SESSION_SOURCE),
          };
        },
      },
    };

    const needRedirect = isPureHost;

    if (needRedirect) {
      landingRoute = {
        path: '/',
        redirect: '/login',
      };
    }

    return [landingRoute, ...commonRoutes];
  },
};
